import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, of } from 'rxjs';
import { map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { MergeStrategy, onWebsocketMessageDispatchUpdateEntities } from '@zerops/zef/entities';
import { UserEntity } from '@zerops/zerops/core/user-base';
import { integrationTokenActions } from './integration-token-base.state';
import { IntegrationTokenApi } from './integration-token.api';
import { IntegrationTokenEntity } from './integration-token.entity';
import { firstAvailable } from '@zerops/zef/core';

@Injectable()
export class IntegrationTokenBaseEffect {
  #actions$ = inject(Actions);
  #api = inject(IntegrationTokenApi);
  #entity = inject(IntegrationTokenEntity);
  #userEntity = inject(UserEntity);

  onLoadTokens$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(integrationTokenActions.loadTokens),
      switchMap((action) => this.#userEntity.activeClientId$.pipe(
        firstAvailable(),
        switchMap((clientId) =>
          this.#api.list$(clientId).pipe(
            map((response) => integrationTokenActions.loadTokensSuccess(response, action)),
            catchError((err) => of(integrationTokenActions.loadTokensFail(err, action)))
          )
        )
      ))
    )
  );

  onLoadTokensSuccess$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(integrationTokenActions.loadTokensSuccess),
      mergeMap((action) => [
        this.#entity.addToCache(action.data.list),
        this.#entity.addIdsToCache(
          action.data.list.map(({ id }) => id),
          { handleGlobally: true }
        )
      ])
    )
  );

  onRegenerateToken$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(integrationTokenActions.regenerateToken),
      withLatestFrom(this.#userEntity.activeClientId$),
      switchMap(([ action, clientId ]) =>
        this.#api.regenerate$(clientId, action.data.tokenId).pipe(
          map((response) => integrationTokenActions.regenerateTokenSuccess(response, action)),
          catchError((err) => of(integrationTokenActions.regenerateTokenFail(err, action)))
        )
      )
    )
  );

  onRegenerateTokenSuccess$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(integrationTokenActions.regenerateTokenSuccess),
      map((action) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { token, ...tokenWithoutSecret } = action.data;
        return this.#entity.addToCache([ tokenWithoutSecret ]);
      })
    )
  );

  // Websocket updates: Replace projects array completely, don't deep merge
  onUpdateStreamMessage$ = createEffect(() =>
    this.#actions$.pipe(
      onWebsocketMessageDispatchUpdateEntities(
        this.#entity,
        {
          zefEntityMergeStrategy: {
            projects: MergeStrategy.KeepNew
          }
        }
      )
    )
  );
}
